import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  LinearProgress,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Divider,
  Typography,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import moment from "moment";
import QualityRankSelect from "./QualityRankSelect";
import PreviewPdfUrl from "../../common/components/PreviewPdfUrl";
import { connect } from "react-redux";
import { evaluateSubmission } from "../../../store/rbm/actions";
import ConfirmEvaluationDialog from "./ConfirmEvaluationDialog";
import ConfirmRejectionDialog from "./ConfirmRejectionDialog";
import { calculateWeightPenalty } from "./rbmCalculations";
import { DescriptionRounded } from "@mui/icons-material";

const PendingReportEvaluationDialog = ({
  open,
  onClose,
  reports,
  selectedReportId = null,
  setPendingReports,
  evaluateSubmission,
}) => {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [report, setReport] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewDoc, setViewDoc] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [evaluationFormData, setEvaluationFormData] = useState({
    achievedTarget: "",
    completed: "",
    accepted: true,
    supervisorComment: "",
    quality: "",
  });
  const [errors, setErrors] = useState({});
  const [showReject, setShowReject] = useState(false);

  useEffect(() => {
    if (open && reports.length > 0) {
      const index = reports.findIndex(
        (report) => report.id === selectedReportId,
      );
      setCurrentIndex(index);
      setReport(reports[index]);
    }
  }, [open, reports, selectedReportId]);

  const resetForm = () => {
    setEvaluationFormData({
      achievedTarget: "",
      completed: "",
      accepted: true,
      supervisorComment: "",
      quality: "",
    });
    setErrors({});
  };

  const formValidator = () => {
    const error = {};
    if (report.targetShare > 0 && evaluationFormData.accepted) {
      if (evaluationFormData.achievedTarget === "") {
        error.achievedTargetErrorMessage =
          "Please specify the achieved target (0 if none).";
      } else if (evaluationFormData.achievedTarget < 0) {
        error.achievedTargetErrorMessage =
          "Achieved target cannot be negative.";
      }
    }

    if (!evaluationFormData.supervisorComment) {
      error.supervisorCommentErrorMessge = "Please provide supervisor comment.";
    }

    if (!evaluationFormData.quality && evaluationFormData.accepted) {
      error.rankErrorMessge = "Please rank the quality of reported work.";
    }

    setErrors(error);
    const hasError = Object.keys(error).length;
    if (hasError) {
      setShowConfirm(false);
    }
    return hasError;
  };

  const handleSubmit = async (next = false) => {
    if (formValidator()) return;

    setIsSubmitting(true);
    try {
      await evaluateSubmission(
        {
          ...evaluationFormData,
          weight: report.weight,
          quality: evaluationFormData.quality || 0,
          targetShare: report.targetShare,
          submissionId: report.submissionId,
          assignmentId: report.id,
          contractId: report.contractId,
          itemId: report.id,
          itemTypeId: report.itemTypeId,
          subordinateId: report.employeeId,
          measurementUnitCode: report.measurementUnitCode,
        },
        setIsSubmitting,
        (status) => {
          if (!status) {
            // remove from pending list
            setPendingReports((prev) => prev.filter((r) => r.id !== report.id));

            // move next if available
            let nextIndex = currentIndex + 1;
            const remaining = reports.filter((r) => r.id !== report.id);

            if (next && remaining.length > 0) {
              const nextReport = remaining[nextIndex] || remaining[0];
              setCurrentIndex(nextIndex >= remaining.length ? 0 : nextIndex);
              setReport(nextReport);
              resetForm();
              onClose();
            } else {
              onClose();
            }
          }
        },
      );
    } catch (error) {}
  };

  // --- SCORE PREVIEW CALCULATION ---
  const computeExpectedScore = () => {
    if (!evaluationFormData.quality) return 0;
    const achieved =
      +report.targetShare > 0
        ? +evaluationFormData.achievedTarget
        : evaluationFormData.completed === "yes"
          ? 100
          : 0;
    const target = +report.targetShare || 100;

    const { expected } = calculateWeightPenalty({
      target,
      achieved,
      weight: +report.weight,
      measurementUnitCode: report.measurementUnitCode,
      quality: evaluationFormData.quality,
    });
    return expected.toFixed(2);
  };

  if (!report) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" color="primary">
              Evaluate Report {currentIndex + 1} of {reports.length}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, lineHeight: 1.6 }}
          >
            <strong>{report.itemTypeName}</strong>{" "}
            <ArrowRightIcon fontSize="small" /> {report.name}
            <br />
            <PersonIcon fontSize="small" /> {report.employeeNames} —{" "}
            {report.positionName}
            <br />
            <span className="text-info">Unit:</span> {report.unitName} |{" "}
            <span className="text-info">Quarter:</span> {report.quarterId} |{" "}
            <span className="text-info">Deadline:</span>{" "}
            {moment(report.endOn).format("ll")}
          </Typography>
          <Divider sx={{ mt: 1 }} />
        </DialogTitle>

        {isSubmitting && <LinearProgress />}

        <DialogContent dividers sx={{ minHeight: "50vh" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2">
              Target:{" "}
              <strong>
                {report.targetShare} {report.measurementUnit}
              </strong>{" "}
              | Achieved:{" "}
              <strong>
                {report.achievedTarget} {report.measurementUnit}
              </strong>
            </Typography>

            <Chip
              label={`Weight: ${report.weightName}`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>

          {/* --- Employee's Report Message --- */}
          <Box
            sx={{
              backgroundColor: "#f9f9f9",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <Box display="flex" alignItems="center" mb={1}>
              <DescriptionRounded
                fontSize="small"
                color="primary"
                sx={{ mr: 1 }}
              />
              <Typography variant="subtitle2" color="primary">
                Report Message
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="textPrimary"
              sx={{ whiteSpace: "pre-line", lineHeight: 1.6 }}
            >
              {report.employeeMessage}
            </Typography>
          </Box>

          {!!report.docReferenceId ? (
            <Button
              size="small"
              onClick={() => setViewDoc(true)}
              startIcon={<AttachFileIcon />}
            >
              Supporting Document <OpenInNewIcon fontSize="small" />
            </Button>
          ) : (
            <span className="badge badge-warning">No file attached</span>
          )}

          <Divider sx={{ my: 2 }} />

          {/* --- Evaluation Fields --- */}
          {report.targetShare > 0 ? (
            <TextField
              error={!!errors.achievedTargetErrorMessage}
              helperText={errors.achievedTargetErrorMessage}
              fullWidth
              size="small"
              type="number"
              label="Achieved Target"
              variant="outlined"
              className="mb-3"
              value={evaluationFormData.achievedTarget}
              onChange={(e) =>
                setEvaluationFormData((prev) => ({
                  ...prev,
                  achievedTarget:
                    e.target.value === "" ? "" : Number(e.target.value),
                }))
              }
            />
          ) : (
            <RadioGroup
              row
              value={evaluationFormData.completed}
              onChange={(e, v) =>
                setEvaluationFormData((prev) => ({ ...prev, completed: v }))
              }
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label="Completed"
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="Not completed"
              />
            </RadioGroup>
          )}

          {evaluationFormData.completed !== "no" && (
            <QualityRankSelect
              value={evaluationFormData.quality}
              onChange={(val) =>
                setEvaluationFormData((prev) => ({ ...prev, quality: val }))
              }
              error={!!errors.rankErrorMessge}
              errorMessage={errors.rankErrorMessge}
            />
          )}

          {/* --- Expected Score Preview --- */}
          {evaluationFormData.accepted && evaluationFormData.quality && (
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary">
                Expected Score (Without penalties):{" "}
                <strong className="text-primary">
                  {computeExpectedScore()}%
                </strong>
              </Typography>
            </Box>
          )}

          <TextField
            error={!!errors.supervisorCommentErrorMessge}
            helperText={errors.supervisorCommentErrorMessge}
            fullWidth
            size="small"
            multiline
            rows={3}
            label="Supervisor Comment"
            variant="outlined"
            className="mt-3"
            value={evaluationFormData.supervisorComment}
            onChange={(e) =>
              setEvaluationFormData((prev) => ({
                ...prev,
                supervisorComment: e.target.value,
              }))
            }
          />
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              setShowReject(true);
            }}
          >
            Reject Report
          </Button>

          <Box display="flex" gap={1}>
            <Button
              onClick={() => {
                setEvaluationFormData((prev) => ({
                  ...prev,
                  accepted: true,
                }));
                setShowConfirm(true);
                formValidator();
              }}
              color="primary"
              variant="contained"
              size="small"
              disabled={isSubmitting}
            >
              {currentIndex < reports.length - 1
                ? "Save & Next"
                : "Save & Close"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      {viewDoc && (
        <PreviewPdfUrl
          showDocument={viewDoc}
          setShowDocument={setViewDoc}
          document={{
            name: "Supporting Document",
            url:
              process.env.REACT_APP_FILES_URL +
              "/preview/" +
              report.docReferenceId,
          }}
        />
      )}

      {showConfirm && (
        <ConfirmEvaluationDialog
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          report={report}
          formData={evaluationFormData}
          onSubmit={() => {
            setShowConfirm(false);
            handleSubmit(true);
            onClose();
          }}
        />
      )}

      {showReject && (
        <ConfirmRejectionDialog
          open={showReject}
          onClose={() => setShowReject(false)}
          report={report}
          onReject={(reason) => {
            setEvaluationFormData((prev) => ({
              ...prev,
              accepted: false,
              supervisorComment: reason,
            }));
            setShowReject(false);
            handleSubmit(false); // or true if you want Save & Next
            onClose();
          }}
        />
      )}
    </>
  );
};

export default connect(null, { evaluateSubmission })(
  PendingReportEvaluationDialog,
);
